(() => {
  const registry = window.NipponVinoWineAtlasRenderers = window.NipponVinoWineAtlasRenderers || {};
  const Base = registry.Base;
  if (!Base) return;

  class RegionenatlasV4Renderer extends Base {
    mount(container, data, state = {}) {
      super.mount(container, data, state);
      this.controller.dataset.mapRenderer = 'regionenatlas-v4';
      this.macroBounds = new Map();
      this.projectors = new Map();
      this.render();
      this.update(this.matchingIds, state);
      this.setLevel(this.level, { animate: false });
    }

    macroDefinitions() {
      return [
        { id: 'hokkaido', title: 'Hokkaidō', subtitle: 'Sapporo · Yoichi', codes: ['01'] },
        { id: 'north-honshu', title: 'Nord-Honshū / Tōhoku', subtitle: 'Iwate · Yamagata', codes: ['02', '03', '04', '05', '06', '07'] },
        { id: 'central-honshu', title: 'Zentral-Honshū', subtitle: 'Tokio · Kōfu · Nagano · Niigata', codes: Array.from({ length: 18 }, (_, index) => String(index + 8).padStart(2, '0')) },
        { id: 'west-honshu', title: 'West-Honshū', subtitle: 'Kansai · Chūgoku · Shikoku', codes: Array.from({ length: 14 }, (_, index) => String(index + 26).padStart(2, '0')) },
        { id: 'kyushu-okinawa', title: 'Kyūshū & Okinawa', subtitle: 'Geografische Orientierung', codes: Array.from({ length: 8 }, (_, index) => String(index + 40).padStart(2, '0')), inactive: true },
      ];
    }

    getLayout() {
      const width = this.mapCanvas?.clientWidth || this.controller.clientWidth || 1200;
      if (width <= 520) {
        return {
          name: 'mobile', width: 360, height: 1510,
          panels: {
            hokkaido: { x: 12, y: 35, width: 336, height: 220 },
            'north-honshu': { x: 12, y: 272, width: 336, height: 235 },
            'central-honshu': { x: 12, y: 524, width: 336, height: 310 },
            'west-honshu': { x: 12, y: 851, width: 336, height: 270 },
            'kyushu-okinawa': { x: 12, y: 1138, width: 336, height: 210 },
          },
          orientation: { x: 52, y: 1365, width: 256, height: 130 },
        };
      }
      if (width <= 900) {
        return {
          name: 'tablet', width: 760, height: 1100,
          panels: {
            hokkaido: { x: 20, y: 40, width: 350, height: 240 },
            'north-honshu': { x: 390, y: 40, width: 350, height: 240 },
            'central-honshu': { x: 20, y: 300, width: 720, height: 330 },
            'west-honshu': { x: 20, y: 650, width: 450, height: 410 },
            'kyushu-okinawa': { x: 490, y: 650, width: 250, height: 240 },
          },
          orientation: { x: 490, y: 910, width: 250, height: 150 },
        };
      }
      return {
        name: 'desktop', width: 1200, height: 760,
        panels: {
          hokkaido: { x: 20, y: 40, width: 250, height: 260 },
          'north-honshu': { x: 290, y: 40, width: 300, height: 260 },
          'central-honshu': { x: 610, y: 40, width: 570, height: 370 },
          'west-honshu': { x: 20, y: 320, width: 570, height: 400 },
          'kyushu-okinawa': { x: 610, y: 430, width: 280, height: 290 },
        },
        orientation: { x: 920, y: 450, width: 260, height: 240 },
      };
    }

    render() {
      const layout = this.getLayout();
      this.layoutName = layout.name;
      this.controller.dataset.atlasLayout = layout.name;
      this.geometryById.clear();
      this.macroBounds.clear();
      this.projectors.clear();
      const svg = this.createSvg(
        layout.width,
        layout.height,
        'Regionenatlas der japanischen Weinregionen',
        'Fünf geografisch projizierte Ausschnitte Japans. Alle Präfekturen lassen sich öffnen.'
      );
      svg.classList.add('wine-atlas-regionenatlas-map');

      this.macroDefinitions().forEach((macro) => {
        const frame = layout.panels[macro.id];
        const features = this.features.filter((feature) => macro.codes.includes(String(feature.properties.code).padStart(2, '0')));
        const projector = this.createProjector(features, {
          x: frame.x + 10, y: frame.y + 40, width: frame.width - 20, height: frame.height - 48,
        }, { padding: 6 });
        this.projectors.set(macro.id, projector);
        this.macroBounds.set(macro.id, { ...frame });
        const panel = this.createElement('g', { class: `atlas-panel${macro.inactive ? ' atlas-panel--inactive' : ''}`, 'data-macro-region': macro.id });
        panel.appendChild(this.createElement('rect', { class: 'atlas-panel-frame', x: frame.x, y: frame.y, width: frame.width, height: frame.height }));
        const title = this.createElement('text', { class: 'atlas-panel-title', x: frame.x + 12, y: frame.y + 18 });
        title.textContent = macro.title;
        const subtitle = this.createElement('text', { class: 'atlas-panel-subtitle', x: frame.x + 12, y: frame.y + 33 });
        subtitle.textContent = macro.subtitle;
        panel.append(title, subtitle);

        const geography = this.createElement('g', { class: 'atlas-panel-geography' });
        features.forEach((feature) => {
          const item = this.regionForFeature(feature);
          const path = this.createElement('path', {
            class: `prefecture${item.region ? ' atlas-profiled' : ''}`,
            d: this.featurePath(feature, projector),
          });
          this.bindInteractive(path, feature);
          const bounds = this.featureBounds(feature, projector);
          if (bounds) this.geometryById.set(item.id, bounds);
          geography.appendChild(path);
        });
        panel.appendChild(geography);
        panel.appendChild(this.renderRoutes(macro.id, projector));
        panel.appendChild(this.renderPlaces(macro.id, projector));
        svg.appendChild(panel);
      });
      svg.appendChild(this.renderOrientation(layout.orientation));
      this.container.replaceChildren(svg);
      this.container.setAttribute('aria-busy', 'false');
      this.svg = svg;
      this.originalViewBox = { x: 0, y: 0, width: layout.width, height: layout.height };
    }

    renderPlaces(macroId, projector) {
      const layer = this.createElement('g', { class: 'atlas-places', 'aria-hidden': 'true' });
      const places = new Map();
      this.regions.filter((region) => region.atlas?.macroRegion === macroId).forEach((region) => {
        [
          { ...region.atlas.referenceCity, kind: 'city' },
          { ...region.atlas.wineHub, kind: 'wine' },
          ...(region.atlas.additionalPoints || []),
        ].filter((place) => Number.isFinite(Number(place.longitude)) && Number.isFinite(Number(place.latitude))).forEach((place) => {
          const key = `${Number(place.longitude).toFixed(4)}:${Number(place.latitude).toFixed(4)}`;
          const existing = places.get(key);
          if (existing) existing.regionIds.add(region.id);
          else places.set(key, { ...place, regionIds: new Set([region.id]) });
        });
      });
      places.forEach((place) => {
        const [x, y] = projector([Number(place.longitude), Number(place.latitude)]);
        const dx = Number(place.labelDx) || 9;
        const dy = Number(place.labelDy) || -9;
        const group = this.createElement('g', { class: `atlas-place atlas-place--${place.kind === 'wine' ? 'wine' : 'city'}`, 'data-region-ids': [...place.regionIds].join(',') });
        group.appendChild(this.createElement('line', { class: 'atlas-place-leader', x1: x, y1: y, x2: x + (dx * 0.72), y2: y + (dy * 0.72) }));
        group.appendChild(this.createElement('circle', { class: 'atlas-place-point', cx: x, cy: y, r: place.kind === 'wine' ? 3.8 : 3.1 }));
        const label = this.createElement('text', { class: 'atlas-place-label', x: x + dx, y: y + dy, 'text-anchor': dx < 0 ? 'end' : 'start' });
        label.textContent = place.name;
        group.appendChild(label);
        layer.appendChild(group);
      });
      return layer;
    }

    renderRoutes(macroId, projector) {
      const layer = this.createElement('g', { class: 'atlas-routes', 'aria-hidden': 'true' });
      this.regions.filter((region) => region.atlas?.macroRegion === macroId).forEach((region) => {
        const fromPoint = region.atlas.referenceCity;
        const toPoint = region.atlas.wineHub;
        if (!fromPoint || !toPoint) return;
        const from = projector([Number(fromPoint.longitude), Number(fromPoint.latitude)]);
        const to = projector([Number(toPoint.longitude), Number(toPoint.latitude)]);
        const group = this.createElement('g', { class: 'atlas-route', 'data-route-region': region.id });
        group.appendChild(this.createElement('line', { x1: from[0], y1: from[1], x2: to[0], y2: to[1] }));
        layer.appendChild(group);
      });
      return layer;
    }

    renderOrientation(frame) {
      const group = this.createElement('g', { class: 'atlas-orientation', 'aria-hidden': 'true' });
      const title = this.createElement('text', { class: 'atlas-orientation-title', x: frame.x, y: frame.y + 12 });
      title.textContent = 'Lage im japanischen Archipel';
      group.appendChild(title);
      const projector = this.createProjector(this.features, { x: frame.x, y: frame.y + 18, width: frame.width, height: frame.height - 18 }, { padding: 5 });
      this.features.forEach((feature) => {
        const code = String(feature.properties.code).padStart(2, '0');
        const macro = this.macroDefinitions().find((item) => item.codes.includes(code));
        group.appendChild(this.createElement('path', {
          class: 'atlas-orientation-piece', d: this.featurePath(feature, projector), 'data-orientation-macro': macro?.id || '',
        }));
      });
      return group;
    }

    update(matchingIds, state = {}) {
      super.update(matchingIds, state);
      const region = this.controller.regionById.get(this.selectedId);
      const selectedMacro = region?.atlas?.macroRegion || '';
      this.svg?.querySelectorAll('[data-macro-region]').forEach((panel) => panel.classList.toggle('is-current', panel.dataset.macroRegion === selectedMacro));
      this.svg?.querySelectorAll('[data-route-region]').forEach((route) => route.classList.toggle('is-active', route.dataset.routeRegion === this.selectedId));
      this.svg?.querySelectorAll('[data-region-ids]').forEach((place) => place.classList.toggle('is-current', String(place.dataset.regionIds).split(',').includes(this.selectedId)));
      this.svg?.querySelectorAll('[data-orientation-macro]').forEach((piece) => piece.classList.toggle('is-current', piece.dataset.orientationMacro === selectedMacro));
    }

    setLevel(level, options = {}) {
      this.level = ['japan', 'region', 'detail'].includes(level) ? level : 'japan';
      const selected = this.geometryById.get(this.selectedId);
      const region = this.controller.regionById.get(this.selectedId);
      const macro = this.macroBounds.get(region?.atlas?.macroRegion);
      const bounds = this.level === 'japan' || !selected ? this.originalViewBox : this.level === 'region' && macro ? macro : selected;
      this.animateViewBox(this.level === 'japan' ? bounds : this.fitBounds(bounds, this.level === 'detail' ? 0.7 : 0.08), options.animate !== false);
    }

    resize() {
      const layout = this.getLayout();
      if (layout.name === this.layoutName) {
        this.setLevel(this.level, { animate: false });
        return;
      }
      const container = this.container;
      const data = this.data;
      const state = { selectedId: this.selectedId, level: this.level, matchingIds: this.matchingIds };
      this.destroy();
      super.mount(container, data, state);
      this.macroBounds = new Map();
      this.projectors = new Map();
      this.render();
      this.update(this.matchingIds, state);
      this.setLevel(this.level, { animate: false });
    }
  }

  registry['regionenatlas-v4'] = RegionenatlasV4Renderer;
})();
